import { createAsyncThunk } from '@reduxjs/toolkit';

import { Moment } from 'moment';
import { AdminApiPaths } from '../../../api/APIPaths';
import APIDataProvider, {
  TApiPaginationResponse,
  TApiResponse,
} from '../../../api/ApiDataProvider';
import { ICourse, TCourseStatus } from '../CoursesSlice';
import { ITrialClass } from '.';

type TGetTrialsForAdmin = {
  limit: number;
  offset: number;
  curriculum?: string;
  subject?: string;
  grade?: string;
  studentId?: string;
  tutorId?: string;
  status?: TCourseStatus;
  startDate?: Moment | null;
  endDate?: Moment | null;
};

export type TAdminCreateTrial = {
  studentId: string;
  curriculum: string;
  subject: string;
  grade: string;
  startDate: string;
  endDate?: string;
  customTime?: string;
  tutorId: string;
};

export type TAdminUpdateTrial = {
  _id: string;
  tutor?: string;
  status?: string;
  dateTime?: string;
};

const apiDataProvider = new APIDataProvider();

export const createTrialAsAdmin = createAsyncThunk(
  '/admin/trials/create',
  async (data: TAdminCreateTrial) => {
    const path = `${AdminApiPaths.trials}`;
    const response = await apiDataProvider.post<TApiResponse<ICourse>>(
      path,
      data
    );

    return response.data;
  }
);

export const updateTrialAsAdmin = createAsyncThunk(
  '/admin/trials/update',
  async ({ _id, ...data }: TAdminUpdateTrial) => {
    const path = `${AdminApiPaths.trials}/${_id}`;
    const response = await apiDataProvider.put<TApiResponse<ICourse>>(
      path,
      data
    );

    return response.data;
  }
);

export const cancelTrialAsAdmin = createAsyncThunk(
  '/admin/trials/cancel',
  async (_id: string) => {
    const path = `${AdminApiPaths.trials}/${_id}`;
    const response = await apiDataProvider.delete<TApiResponse<ICourse>>(path);

    return response.data;
  }
);

const getTrialsForAdminModule = async (data: TGetTrialsForAdmin) => {
  let path = `${AdminApiPaths.trials}?limit=${data.limit}&offset=${data.offset}`;

  if (data.curriculum) {
    path += `&curriculum=${data.curriculum}`;
  }

  if (data.subject) {
    path += `&subject=${data.subject}`;
  }

  if (data.grade) {
    path += `&grade=${data.grade}`;
  }

  if (data.studentId) {
    path += `&studentId=${data.studentId}`;
  }

  if (data.tutorId) {
    path += `&tutorId=${data.tutorId}`;
  }

  if (data.status) {
    path += `&status=${data.status}`;
  }

  if (data.startDate) {
    path += `&start=${data.startDate.toISOString()}`;
  }

  if (data.endDate) {
    path += `&end=${data.endDate.toISOString()}`;
  }

  const response = await apiDataProvider.get<
    TApiResponse<TApiPaginationResponse<ITrialClass>>
  >(path);

  return response.data;
};

export const getTrialsForAdmin = createAsyncThunk(
  '/admin/trials/list',
  getTrialsForAdminModule
);

export const loadMoreTrialsForAdmin = createAsyncThunk(
  '/admin/trials/loadMore',
  getTrialsForAdminModule
);
